import {produce} from 'immer';
import React from 'react';

import {
  type ArtifactVersionSelectionDataQuery,
  useArtifactVersionSelectionDataQuery,
} from '../../generated/graphql';
import {NetworkStatus} from '../../state/graphql/apolloWithAbort';
import {propagateErrorsContext} from '../../util/errors';
import {type ArtifactVersionsTableData} from '../ArtifactOverview/hooks';
import {useBufferedPageController} from '../Automations/tabs/useBufferedPageController';
import {type ModifiedTag} from '../TagAssignment';
import {
  ARTIFACT_VERSION_SELECTION_FETCH_PAGE_SIZE,
  ARTIFACT_VERSION_SELECTION_PAGE_SIZE,
} from './versionSelectionUtils';

type UseArtifactVersionSelectionDataArgs = {
  artifactCollectionId?: string;
  searchTerm: string;
};

export function useArtifactVersionSelectionData({
  artifactCollectionId,
  searchTerm,
}: UseArtifactVersionSelectionDataArgs) {
  const [requestPage, setRequestPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(
    ARTIFACT_VERSION_SELECTION_PAGE_SIZE
  );
  const isFetchingMoreRef = React.useRef(false);
  const trimmedSearchTerm = searchTerm.trim();
  const skip = !artifactCollectionId;

  const {data, loading, fetchMore, networkStatus, error} =
    useArtifactVersionSelectionDataQuery({
      variables: {
        artifactCollectionID: artifactCollectionId ?? '',
        limit: ARTIFACT_VERSION_SELECTION_FETCH_PAGE_SIZE,
        searchTerm: trimmedSearchTerm || undefined,
      },
      context: propagateErrorsContext(),
      notifyOnNetworkStatusChange: true,
      skip,
    });

  const membershipConnection = data?.artifactCollection?.artifactMemberships;
  const rows = React.useMemo<ArtifactVersionsTableData[]>(
    () => parseArtifactVersionSelectionData(data),
    [data]
  );
  const pageInfo = membershipConnection?.pageInfo;
  const endCursor = pageInfo?.endCursor ?? undefined;
  const hasNextPage = pageInfo?.hasNextPage === true && endCursor != null;
  const fetchingNextPage = networkStatus === NetworkStatus.fetchMore;
  const isInitialLoading =
    loading &&
    (networkStatus === NetworkStatus.loading ||
      networkStatus === NetworkStatus.setVariables);

  const fetchNextPage = React.useCallback(() => {
    if (
      !artifactCollectionId ||
      !hasNextPage ||
      isFetchingMoreRef.current ||
      endCursor == null
    ) {
      return;
    }
    isFetchingMoreRef.current = true;

    void Promise.resolve(
      fetchMore({
        variables: {
          artifactCollectionID: artifactCollectionId,
          limit: ARTIFACT_VERSION_SELECTION_FETCH_PAGE_SIZE,
          after: endCursor,
          searchTerm: trimmedSearchTerm || undefined,
        },
        updateQuery: (previousResult, {fetchMoreResult}) => {
          const nextConnection =
            fetchMoreResult?.artifactCollection?.artifactMemberships;
          if (!nextConnection) {
            return previousResult;
          }

          return produce(previousResult, draft => {
            const previousConnection =
              draft.artifactCollection?.artifactMemberships;
            if (!previousConnection) {
              return;
            }

            previousConnection.edges.push(...nextConnection.edges);
            previousConnection.pageInfo = nextConnection.pageInfo;
          });
        },
      })
    ).finally(() => {
      isFetchingMoreRef.current = false;
    });
  }, [
    artifactCollectionId,
    endCursor,
    fetchMore,
    hasNextPage,
    trimmedSearchTerm,
  ]);

  const pageController = useBufferedPageController({
    source: {
      rows,
      loading: isInitialLoading,
      error,
      hasNextPage,
      fetchingNextPage,
      fetchNextPage,
    },
    requestPage,
    pageSize,
    setRequestPage,
    setPageSize,
    resetKey: JSON.stringify({artifactCollectionId, trimmedSearchTerm}),
  });

  return {
    ...pageController,
    rowCount: pageController.hasNextPage
      ? pageController.allRows.length + 1
      : pageController.allRows.length,
    loading: pageController.loading || fetchingNextPage,
  };
}

// The metrics grid only needs enough version data to render/select rows.
// Do not reuse the full artifact versions table parser here: that query pulls
// large fields like runHistory and metadata, which can be slow for versions
// produced by runs with many logged metrics.
function parseArtifactVersionSelectionData(
  data?: ArtifactVersionSelectionDataQuery
): ArtifactVersionsTableData[] {
  const membershipEdges = data?.artifactCollection?.artifactMemberships.edges;
  if (membershipEdges == null) {
    return [];
  }

  return membershipEdges
    .map(edge => ({
      membershipId: edge.node?.id ?? '',
      id: edge.node?.artifact?.id ?? '',
      versionIndex: edge.node?.versionIndex ?? 0,
      tags: edge.node?.artifact?.tags?.map(tag => tag as ModifiedTag) ?? [],
      metadata: {},
      ttlDurationSeconds: 0,
      usedCount: 0,
      size: 0,
      aliases: edge.node?.aliases.map(alias => alias.alias) ?? [],
      isGenerated: false,
      createdAt: edge.node?.artifact?.createdAt ?? undefined,
      linkedAt: edge.node?.createdAt ?? undefined,
      runHistory: {},
    }))
    .filter(row => row.membershipId);
}
